import * as React from "react";
import { useContentFramingStore } from "@/state/contentFramingStore";

type SlideShape = {
  id: string;
  slideId: string;
  name: string;
  top: number;
  left: number;
  height: number;
  width: number;
  label?: string;
};

type SlideShapesData = {
  slideId: string;
  width: number;
  height: number;
  shapes: SlideShape[];
  targetShapeId?: string | null;
};

const TARGET_ASPECT = 16 / 9;
const TARGET_WIDTH = 640; // px
const TARGET_HEIGHT = Math.round(TARGET_WIDTH / TARGET_ASPECT);

// Function to calculate dynamic dimensions based on container width
const calculateDynamicDimensions = (containerWidth: number, aspectRatio: number = TARGET_ASPECT) => {
  const maxWidth = Math.min(containerWidth * 1); // Max 90% of container or 800px
  const minWidth = 300; // Minimum width
  const calculatedWidth = Math.max(minWidth, maxWidth);
  const calculatedHeight = Math.round(calculatedWidth / aspectRatio);
  
  return {
    width: calculatedWidth,
    height: calculatedHeight,
    scale: calculatedWidth / TARGET_WIDTH // Scale factor for shapes
  };
};

// Export function to get dynamic dimensions for external use
export const getDynamicDimensions = (containerWidth: number) => {
  return calculateDynamicDimensions(containerWidth);
};

// Hover adjustments
const HOVER_HEIGHT_MULT = 2;
const HOVER_LABEL_MULT = 1.2;

function adjustHoverRect(
  topPx: number,
  heightPx: number,
  containerHeightPx: number,
  heightMultiplier: number
) {
  const currentBottom = topPx + heightPx;
  const newHeight = Math.round(heightPx * heightMultiplier);
  // If expanding would overflow bottom, anchor to bottom and grow upward
  if (topPx + newHeight > containerHeightPx) {
    const newTop = Math.max(0, currentBottom - newHeight);
    return { top: newTop, height: newHeight };
  }
  // Otherwise, anchor to top and grow downward
  return { top: topPx, height: newHeight };
}

export function SlideWireframe() {
  const [data, setData] = React.useState<SlideShapesData | null>(null);
  const [targetId, setTargetId] = React.useState<string | null>(null);
  const [containerWidth, setContainerWidth] = React.useState<number>(0);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const hoveredShapeId = useContentFramingStore((state) => state.hoveredShapeId);
  const setHoveredShapeId = useContentFramingStore((state) => state.setHoveredShapeId);
  const storeShapes = useContentFramingStore((state) => state.slideShapesData);
  const slideDimensions = useContentFramingStore((state) => state.slideDimensions);
  const rows = useContentFramingStore((state) => state.rows);
  console.log("hoveredShapeId", hoveredShapeId);

  // Build data from content framing store instead of localStorage
  React.useEffect(() => {
    const targetShapeId = storeShapes?.find(r => r.label === 'Target Shape')?.id || null;
    const nextData: SlideShapesData = {
      slideId: storeShapes?.[0]?.slideId || "",
      width: slideDimensions?.width || TARGET_WIDTH,
      height: slideDimensions?.height || TARGET_HEIGHT,
      shapes: storeShapes || [],
      targetShapeId: targetShapeId,
    };
    setData(nextData);
    setTargetId(targetShapeId);
  }, [storeShapes, rows, slideDimensions]);

  // Track container width changes
  React.useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    // Initial measurement
    updateContainerWidth();

    // Set up resize observer
    const resizeObserver = new ResizeObserver(updateContainerWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Fallback for window resize
    window.addEventListener('resize', updateContainerWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateContainerWidth);
    };
  }, []);

  const { scaledWidth, scaledHeight, scale, dynamicWidth, dynamicHeight } = React.useMemo(() => {
    // Calculate dynamic dimensions based on container width
    const dynamic = calculateDynamicDimensions(containerWidth);
    
    if (!data) {
      return { 
        scaledWidth: dynamic.width, 
        scaledHeight: dynamic.height, 
        scale: dynamic.scale,
        dynamicWidth: dynamic.width,
        dynamicHeight: dynamic.height
      };
    }
    
    const originalWidth = Math.max(1, Number(data.width) || TARGET_WIDTH);
    const originalHeight = Math.max(1, Number(data.height) || TARGET_HEIGHT);
    const scaleX = dynamic.width / originalWidth;
    const scaleY = dynamic.height / originalHeight;
    const finalScale = Math.min(scaleX, scaleY);
    
    return {
      scaledWidth: Math.round(originalWidth * finalScale),
      scaledHeight: Math.round(originalHeight * finalScale),
      scale: finalScale,
      dynamicWidth: dynamic.width,
      dynamicHeight: dynamic.height
    };
  }, [data, containerWidth]);

  return (
    <div className="mt-4" ref={containerRef}>
      <div
        className="relative mx-auto bg-white"
        style={{
          width: `${dynamicWidth}px`,
          height: `${dynamicHeight}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          className="relative bg-white"
          style={{
            width: `${scaledWidth}px`,
            height: `${scaledHeight}px`,
            overflow: "hidden",
            border: "3px solid black",
          }}
        >
          {data?.shapes
            ?.sort((a, b) => b.width - a.width)
            .map((s) => {
              const left = Math.round(s.left * scale);
              const top = Math.round(s.top * scale);
              const width = Math.round(s.width * scale);
              const height = Math.max(Math.round(s.height * scale), 15);
              const baseLabelFontSize = s.name.length > 10 ? Math.round(9 * scale * 1.4) : Math.round(11 * scale * 1.4);
              const isTarget = targetId === s.id;
              const isHovered = hoveredShapeId === s.id;
              const rect = (isHovered && !isTarget)
                ? adjustHoverRect(top, height, scaledHeight, HOVER_HEIGHT_MULT)
                : { top, height };

              return (
                <div
                  key={s.id}
                  className="absolute overflow-hidden cursor-pointer"
                  onMouseEnter={() => { if (!isTarget) setHoveredShapeId(s.id); }}
                  onMouseLeave={() => { if (hoveredShapeId === s.id) setHoveredShapeId(null); }}
                  style={{
                    left: `${left}px`,
                    top: `${rect.top}px`,
                    width: `${width}px`,
                    height: `${rect.height}px`,
                    zIndex: (isHovered && !isTarget) ? 10 : 1,
                    border: isTarget 
                      ? "2px solid  var(--color-as-orange-primary)" 
                      : isHovered 
                        ? "3px dotted var(--color-as-yellow)" 
                        : "2px solid black",
                    backgroundColor: (isHovered && !isTarget) ? 'var(--color-as-light-yellow)' : 'transparent',
                  }}
                >
                  <div
                    className={`pointer-events-none select-none leading-tight ${
                      isTarget 
                      ? "text-as-orange-primary font-bold" 
                        : "text-black"
                    }`}
                    style={{
                      fontSize: `${(isHovered && !isTarget) ? Math.round(baseLabelFontSize * HOVER_LABEL_MULT) : baseLabelFontSize}px`,
                      padding: "0px 6px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100%",
                      color: (isHovered && !isTarget) ? 'var(--color-as-yellow)' : undefined,
                    }}
                  >
                    {s.label}
                    {isTarget && (
                      <div style={{ marginTop: `${10 * scale}px` }}>
                        <div>New AlphaScript Content Added here.</div>
                        <div>Share Price Performance - Line Chart</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

export default SlideWireframe;


